RivalIQ.apps.Insights.module('widgets', function (widgets, App, Backbone, Marionette, $, _, s, moment) {

    'use strict';

    const MAX_PPT_EXPORT_COLUMNS = 6;

    function isInsightsCuration(curation) {
        return _.includes([
            RivalIQ.enums.curation.TWITTER_INSIGHTS_POSTS,
            RivalIQ.enums.curation.LINKEDIN_INSIGHTS_POSTS,
            RivalIQ.enums.curation.FACEBOOK_INSIGHTS_POSTS,
            RivalIQ.enums.curation.INSTAGRAM_INSIGHTS_POSTS,
            RivalIQ.enums.curation.INSTAGRAM_INSIGHTS_STORIES,
            RivalIQ.enums.curation.INSTAGRAM_INSIGHTS_STORY_DAY
        ], curation.id);
    }

    function getLimit(widget, layout, limit) {
        const curation = widget.getCuration();

        if (!widget.isFocus() && isInsightsCuration(curation)) {
            limit = 3;
            if (layout && layout !== 'list' && layout !== 'top_bottom_posts' && layout !== 'top_bottom_posts_with_details') {
                limit = 4;
            }
        }

        return limit;
    }

    const showImageLoadingToast = _.memoize(function (dashboardModel) {
        if (!dashboardModel.isExporting() && !dashboardModel.getProp('isInPostTagEditMode') && !dashboardModel.getProp('suppressBrokenImageNotifications')) {
            RivalIQ.execute('alert:info', RivalIQ.strings.social_posts_loading_broken_images);

            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.SocialPostsBrokenImageToast, {
                accountUserId: RivalIQ.state.user.get('id'),
                landscapeId: dashboardModel.getProp('landscapeId'),
                timePeriod: dashboardModel.getProp('timePeriod')
            });
        }
    }, function (dashboardModel) {
        let timePeriodStr = '';
        const timePeriod = dashboardModel.getProp('timePeriod');
        if (timePeriod) {
            // startOf day since some time periods have second components.
            timePeriodStr = [
                moment.utc(timePeriod.mainPeriodStart).startOf('day').toISOString(),
                moment.utc(timePeriod.mainPeriodEnd).startOf('day').toISOString()
            ].join(';');
        }
        return timePeriodStr;
    });

    function getFacebookAdAccountCurrency(dashboardModel) {
        const adAccount = _.head(dashboardModel.curation.activeFacebookAdAccounts);
        return _.get(adAccount, ['currency']);
    }

    // We need to find the post metric; with mobile view and post tag edit modes,
    // it's not always at the same index, so use the metric collection to figure out where it is.
    function findPostMetric(collection) {
        return collection.find(function (m) { return m.get('property') === 'post'; });
    }

    App.widgets.TableView = RivalIQ.apps.Dashboard.widgets.MetricTable.mixin({

        mixins: {
            LoadDataWithBuffering: RivalIQ.apps.Dashboard.mixins.LoadDataWithBuffering,
            mediaLoadErrorHandler: widgets.media.MediaLoadErrorHandler,
            PostTagHelpersMixin: RivalIQ.views.PostTagHelpersMixin,
            postTagsOnTable: RivalIQ.apps.Dashboard.mixins.PostTagsOnTable,
            postTagsReload: RivalIQ.apps.SocialPosts.mixins.PostTagsReload
        },

        // NOTE: these functions are included in mixins.PostTagsOnTable
        postTagManagerEvents: {
            'postTag:changed': 'onPostTagChanged',
            'postTag:deleted': 'onPostTagDeleted',
            'postTag:applied': 'onPostTagApplicationChange',
            'postTag:unapplied': 'onPostTagApplicationChange',
            'postTag:created': 'onPostTagCreated'
        },

        shouldShowHeaderSourceIcons: _.constant(false),

        getFetchAttributes: function () {
            const me = this;

            const metrics = me.metricsCollection;

            const orderBy = me.getProp('orderBy');
            let orderByIdx;

            // grid or mosaics are organized by property (string)
            if (_.isString(orderBy)) {
                orderByIdx = metrics.indexOf(metrics.findWhere({
                    property: orderBy
                }));
            } else {
                orderByIdx = Number(orderBy);
            }

            me.sortColumns = metrics.getColumnSortOrder(orderByIdx);

            return {
                limit: Number(me.getProp('limit') || 0),
                sortOrder: Number(me.getProp('sortOrder') || 1),
                orderBy: metrics.at(me.sortColumns[0]).get('property')
            };
        },

        onBeforeRenderLoaded: function () {
            const me = this;
            const showPostTags = me.getProp('showPostTags');

            RivalIQ.postTags.utils.updateBodyClass(undefined, showPostTags);
        },

        onRenderLoaded: function () {
            const me = this;
            RivalIQ.views.SocialPostGrid.prototype.onRenderLoaded.apply(me, arguments);
        },

        onAttachLoaded: function () {
            const me = this;

            RivalIQ.apps.Dashboard.widgets.MetricTable.prototype.onAttachLoaded.apply(me, arguments);
            RivalIQ.apps.SocialPosts.utils.attachVideoOnLoadHandlers(me.$el);
        },

        _loadData: App.widgets.media.utils.wrapLoadDataForCustomDashboards(function () {
            const me = this;

            // we'll set up table metrics later when we can determine curation presence, see #27023
            if (widgetIsCustomDashboard(me)) {
                setupMetrics(me.dashboardModel, me.getProps());
            }
            me.metricsCollection = me.dashboardModel.metricsCollection;
            me.metrics ??= me.dashboardModel.metrics;
            me.metric ??= me.dashboardModel.metric;
            const parentLoad = RivalIQ.apps.Dashboard.widgets.MetricTable.prototype.loadData.apply(me, arguments);

            let fetches;

            // NOTE: no post tag data for instagram stories or story days
            // or if user is not in PDV2 beta
            if (_.includes(me.metric.dataSource, 'instagramStory')) {
                fetches = [parentLoad];
            } else if (me.getProp('isInPostTagEditMode')) {
                fetches = [
                    RivalIQ.data.companyData.fetchPresences(),
                    RivalIQ.postTags.data.PostTags.fetch(),
                    me.getNumPostsCountsMetricDef(me.getProps()).fetch(),
                    me.getPostTagCountsMetricDef(me.getProps()).fetch()
                ];
            } else {
                fetches = [
                    parentLoad,
                    RivalIQ.postTags.data.PostTags.fetch()
                ];
            }

            return $.when.apply($, fetches).done(function (companyData, postTags, numPosts, postTagCounts) {
                if (!_.isUndefined(postTags) && me.collection) {
                    me.postTagsById = _.keyBy(postTags, 'id');
                    const postMetric = findPostMetric(me.collection);
                    const posts = _.map(postMetric.getValue(), _.last);
                    me.dashboardModel.setLoadedPosts(posts);

                    if (me.getProp('isInPostTagEditMode')) {
                        me.dashboardModel.setTotalNumPosts(numPosts.getValue({
                            format: false
                        }));
                        me.dashboardModel.setPostTagCounts(postTagCounts.getValue({
                            format: false
                        }));
                        me.dashboardModel.companyData = companyData;
                    }
                }
            });
        }),

        rowClassName: function () {
            const me = this;
            return (me.getProp('noClickThrough') ? '' : 'pointer');
        },

        onRowClick: function (data, e) {
            const me = this;

            const target = $(e.target);

            if (target.hasClass('post-tag-action-btn') || target.parents('.post-tag-action-btn').length) {
                e.stopPropagation();
                return;
            }

            if (target.is('a') || me.getProp('noClickThrough')) {
                // don't pop the modal if the user clicked on a link.
                return;
            }

            const objectType = me.getProp('objectType');
            if (objectType === RivalIQ.enums.insightsObjectType.storyDay) {
                const storyDay = data[0][0];
                App.widgets.media.utils.navToStoryDay(me.dashboardModel, storyDay);
            } else {
                // The second column of these tables is always the object details.
                const post = data[1][1];
                const metric = me.collection.at(1);
                App.widgets.media.utils.showDetails(me.dashboardModel, post, metric, me.postTagsById);
            }
        },

        emptyViewOptions: function () {
            const me = this;

            const elementName = (me.getProp('objectType') === RivalIQ.enums.insightsObjectType.post ? 'posts' : 'stories');

            return {
                elementName: s.capitalize(elementName)
            };
        },

        getEmptyView: function () {
            const me = this;
            const widget = (me.dashboardModel || me);

            if (widget.getCuration().isCustomDashboard && me._no_load_attempted) {
                let template = (widget.getProp('companyId') ? '#insightsCustomDashboardsNoFocusCompanyTemplate' : '#insightsCustomDashboardsNoLandscapeCompaniesTemplate');
                if (RivalIQ.props.channelRedacted(widget.getProps(), widget.getProp('source'))) {
                    template = '#insightsCustomDashboardsNoAccessTemplate';
                }

                return RivalIQ.views.ItemView.extend({
                    className: 'grid-view-empty',
                    template: template,
                    templateHelpers: function () {
                        const me = this;
                        return {
                            elementName: me.options.elementName,
                            sourceName: _.get(RivalIQ.enums.sourceDetails, [widget.getProp('source'), 'name'])
                        };
                    }
                });
            }

            return RivalIQ.apps.Dashboard.widgets.MetricTable.prototype.getEmptyView.apply(me, arguments);
        }
    });

    const StoryCell = Marionette.ItemView.extend({
        tagName: 'td',
        template: '#instagramStoryCellTemplate',
        className: 'post-overview story-overview',

        templateHelpers: function () {
            return _.extend({
                s: s
            }, RivalIQ.utils.emoji.getTemplateHelpers());
        },

        serializeData: function () {
            const me = this;

            const post = _.last(me.getOption('data'));

            return _.defaults({
                maxMessageLength: App.widgets.media.utils.MAX_BODY_LENGTH,
                presence_icon_url: post.presence_icon_url,
                image: post.image,
                message: post.caption || null
            }, post);
        }
    });

    const StoryDayCell = Marionette.ItemView.extend({
        tagName: 'td',
        template: '#instagramStoryDayCellTemplate',
        className: 'story-day-overview',

        templateHelpers: function () {
            return _.extend({
                s: s
            }, RivalIQ.utils.emoji.getTemplateHelpers());
        },

        serializeData: function () {
            const me = this;

            const post = _.last(me.getOption('data'));

            return post;
        }
    });

    const StoryDayDate = {
        tagName: 'td',
        render: function (metric, data) {
            const date = moment.utc(_.last(data));
            const timezone = metric.get('timezone');
            return Marionette.TemplateCache.get('#storyPublishedAtCellTemplate')({
                dateStr: date.format('MMM D, YYYY'),
                timeStr: '',
                tz: RivalIQ.utils.timezone.getTimezoneAbbreviation(timezone, date)
            });
        }
    };

    const StoryPublishedAt = {
        tagName: 'td',
        render: function (metric, data) {
            const date = _.last(data);
            const timezone = metric.get('timezone');
            const dateInTz = RivalIQ.utils.timezone.getDateInTimezone(timezone, date);

            return Marionette.TemplateCache.get('#storyPublishedAtCellTemplate')({
                dateStr: dateInTz.format('MMM D, YYYY'),
                timeStr: dateInTz.format('LT'),
                tz: RivalIQ.utils.timezone.getTimezoneAbbreviation(timezone, date)
            });
        }
    };

    const PostCell = Marionette.ItemView.extend({
        tagName: 'td',
        template: '#insightsPostCellTemplate',
        className: 'post-overview private-post-overview',

        templateHelpers: function () {
            const me = this;
            const dashboardModel = me.getOption('table').dashboardModel;
            return _.extend({
                s: s,
                isExporting: dashboardModel.isExporting()
            }, App.utils.currencyTemplateHelpers(dashboardModel), RivalIQ.utils.emoji.getTemplateHelpers());
        },

        serializeData: function () {
            const me = this;

            const post = _.last(me.getOption('data'));
            let image = post.image || post.platformImageUrl;

            let videoSrc = null;
            if (App.widgets.media.utils.useLinkedInMp4(post)) {
                videoSrc = post.media_url;
                image = null;
            }

            return _.defaults({
                channelName: _.upperFirst(RivalIQ.enums.sourceDetails[post.source].channel),
                maxMessageLength: App.widgets.media.utils.MAX_BODY_LENGTH,
                maxLinkTitleLength: App.widgets.media.utils.MAX_LINK_TITLE_LENGTH,
                presence_icon_url: post.presence_icon_url,
                image: image,
                videoSrc: videoSrc,
                message: post.caption || null,
                paid_spend: post.paid_spend || null,
                postId: [post.source, post.id].join('.')
            }, post, {
                link: null,
                last_pull_attempt_at: post.captured_at || null,
                isFocus: (post.company_id === RivalIQ.state.utils.getFocusedCompanyId()),
                presence_link: `/company/${post.company_id}`,
                postLink: _.get(post, ['rivaliq', 'display', 'postLink']),
                useCdnThumbnail: _.get(post, 'useCdnThumbnail', false)
            });
        }
    });

    // These columns are always present
    const staticColumns = {
        story: [
            {
                property: 'published_at',
                cellView: _.constant(StoryPublishedAt),
                header: {
                    className: 'post-published-timezone'
                }
            },
            {
                property: 'story',
                name: 'Story',
                sortable: false,
                cellView: _.constant(StoryCell),
                header: {
                    className: 'post-primary-column'
                }
            }
        ],
        storyDay: [
            {
                name: 'Date',
                property: 'story_day_date',
                cellView: _.constant(StoryDayDate),
                header: {
                    className: 'post-published-timezone'
                }
            },
            {
                property: 'storyDay',
                name: 'Stories',
                sortable: false,
                cellView: _.constant(StoryDayCell),
                header: {
                    className: 'post-primary-column'
                }
            }
        ],
        post: [
            {
                property: 'published_at',
                cellView: _.constant(StoryPublishedAt),
                header: {
                    className: 'post-published-timezone'
                }
            },
            {
                property: 'post',
                name: 'Post',
                sortable: false,
                cellView: _.constant(PostCell),
                header: {
                    className: 'post-primary-column'
                }
            }
        ]
    };

    function widgetIsCustomDashboard(widget) {
        return (widget.getCuration ? widget.getCuration() : widget.dashboardModel.getCuration()).isCustomDashboard;
    }

    function setupMetrics(widget, props) {
        let definitions;

        widget.baseMetric = App.utils.createInsightsMetric(props.objectType, props);
        widget.metric = new RivalIQ.models.MetricDefinition(_.clone(widget.baseMetric));

        const hasPaidEngagement = App.utils.hasInstagramPaidEngagement(widget);
        const isPostObject = (props.objectType === RivalIQ.enums.insightsObjectType.post);

        if (!widgetIsCustomDashboard(widget)) {
            const isFocus = (!props.modalWidget && widget.getCuration().isFocus());
            definitions = App.utils.getWidgetDefinitions(props, isFocus);
        } else {
            // NOTE in custom dashboards we want to retain only the `property`
            // as the dimensions will get wonky below if we pass them through
            // exactly as we've received them
            definitions = _.map(widget.getProp('metrics'), function (metric) {
                return _.pick(metric, 'property');
            });
        }

        definitions = _.clone(staticColumns[props.objectType]).concat(definitions);
        const built = _.map(definitions, function (config) {
            if (_.isString(config)) {
                config = {
                    property: config
                };
            }

            let attrs = config;
            if (isPostObject && props.source === RivalIQ.enums.sources.INSTAGRAM_INSIGHTS) {
                attrs = _.defaults({
                    property: App.utils.getInstagramPaidEngagementMappedProperty(config.property, hasPaidEngagement)
                }, config);
            }

            const definition = (new RivalIQ.models.MetricDefinition(attrs)).toJSON();
            return widget.metric.build(_.defaults(definition, _.pick(props, 'orderBy')));
        });

        // make sure the orderBy column is visible.
        const orderBy = _.get(props, 'orderBy');
        const isGridLayout = RivalIQ.apps.SocialPosts.utils.isGridLayoutFromProps(props) || RivalIQ.apps.SocialPosts.utils.isTopBottomLayoutFromProps(props);
        if (!_.isUndefined(orderBy) && props.columnLimit && orderBy >= props.columnLimit) {
            const orderByMetric = built[orderBy];
            props.orderBy = props.columnLimit - 1;
            if (orderByMetric) {
                built.splice(orderBy, 1);
                built.splice(props.orderBy, 0, orderByMetric);
            }
        } else if (!_.isUndefined(orderBy) && !_.isNumber(orderBy)) {

            if (isGridLayout) {
                // Make sure that the orderBy is valid for this objectType.
                const validProperties = widget.getSortProperties(props.objectType);
                if (!_.includes(validProperties, orderBy)) {
                    props.orderBy = _.head(validProperties);
                }
            } else {
                const definitionFound = Boolean(_.find(definitions, function (def) {
                    const defProperty = def.property || def;
                    return defProperty === orderBy;
                }));

                // If orderBy is a property that is not in the list, change orderBy to be the first col.
                if (!definitionFound) {
                    props.orderBy = _.get(definitions, ['0', 'property']);
                }

                if (!props.orderBy) {
                    props.orderBy = _.head(widget.getSortProperties(props.objectType));
                }
            }
        }

        widget.metrics = built;
        widget.metricsCollection = new RivalIQ.collections.MetricDefinitions(widget.metrics);
    }

    const MAX_POSTS_IN_TABLE = 200;
    const PanelFooter = RivalIQ.apps.Dashboard.views.RowCountPanelFooter.mixin({
        template: '#postsPanelPanelFooterTemplate',

        ui: _.defaults({
            infoBubbles: '.fa-info-circle'
        }, RivalIQ.apps.Dashboard.views.RowCountPanelFooter.prototype.ui),

        shouldShowRowCountSummary: function () {
            const me = this;
            return !me.dashboardModel.hasViewMore();
        },

        loadData: function () {
            const me = this;

            const countProperty = me.getProp('objectType') === RivalIQ.enums.insightsObjectType.storyDay ? 'days_with_story' : 'count';
            const props = me.getProps();
            const metric = new RivalIQ.models.MetricDefinition(App.utils.createInsightsMetric(props.objectType, props)).build({
                property: countProperty,
                dimensions: ['channel', 'companyId', 'timeAnalysisMode'],
                orderBy: undefined
            }, {
                clobberDimensions: true
            });
            return metric.fetch().done(function (numPosts) {
                me.numPostsResults = numPosts;
                me.totalCount = numPosts.getValue();
                me.showingCount = Math.min(MAX_POSTS_IN_TABLE, numPosts.getValue({
                    format: false
                }));
            });
        },

        // Override the error behavior as the default onLoadDataError will fail exports.
        onLoadDataError: _.noop,

        serializeData: function () {
            const me = this;

            let objectTypeName;
            let objectTypeNamePlural;
            switch (me.getProp('objectType')) {
                case RivalIQ.enums.insightsObjectType.story:
                    objectTypeNamePlural = 'frames';
                    objectTypeName = 'frame';
                    break;

                case RivalIQ.enums.insightsObjectType.storyDay:
                    objectTypeNamePlural = 'stories';
                    objectTypeName = 'story';
                    break;

                case RivalIQ.enums.insightsObjectType.post:
                default:
                    objectTypeName = RivalIQ.data.channels.get(me.dashboardModel.getProp('source')).get('postName');
                    objectTypeNamePlural = `${objectTypeName}s`;
                    break;
            }

            objectTypeName = RivalIQ.formatter.numericLabel(objectTypeName, objectTypeNamePlural, me.numPostsResults.getValue({
                format: false
            }));

            return {
                popoverText: `Rival IQ displays a max of ${MAX_POSTS_IN_TABLE} ${objectTypeNamePlural}. To view other ${objectTypeNamePlural}, adjust your date range and filters.`,
                postWord: objectTypeName
            };
        },

        onRenderLoaded: function () {
            const me = this;

            RivalIQ.apps.Dashboard.views.RowCountPanelFooter.prototype.onRenderLoaded.call(me);

            me.ui.infoBubbles.popover();
        }
    });

    const MediaWidget = RivalIQ.apps.Insights.widgets.MediaWidget = RivalIQ.apps.Dashboard.widgets.TableWidget.mixin({
        getExportTitle: App.utils.insightsExportTitle,
        getExportSubtitle: App.utils.insightsExportSubtitle,
        getPptSubtitle: App.utils.insightsPptSubtitle,
        scheduledExportAdvancedSettingsEnabled: false,

        view: App.widgets.media.MediaViewContainer,
        panelView: App.widgets.media.MediaViewPanel,
        detailPanelView: App.widgets.media.MediaViewPanel,
        footerView: function (curation) {
            if (RivalIQ.apps.SocialPosts.utils.isTopBottomLayout(curation.dashboardModel)) {
                return new RivalIQ.views.SocialPostsTopBottomFooter(curation);
            }
            return new PanelFooter(curation);
        },
        getExportDropdownView: _.constant(RivalIQ.apps.SocialPosts.widgets.ExportDropdown),

        mixins: {
            focusCompanyTitlePlacementMixin: RivalIQ.apps.Dashboard.mixins.FocusCompanyTitlePlacementMixin,
            brokenThumbnailHandler: RivalIQ.utils.BrokenThumbnailHandler,
            postSelection: RivalIQ.postTags.mixins.PostSelectionMixin
        },

        initialize: function () {
            const me = this;

            me.listenTo(me, 'brokenpostimage', function () {
                showImageLoadingToast(me);
            });

            RivalIQ.apps.Dashboard.widgets.TableWidget.prototype.initialize.apply(me, arguments);
        },

        propValues: {
            columnWidth: [12]
        },

        title: function () {
            const me = this;

            let title = me.getProp('title');
            if (title) {
                return title;
            }

            switch (me.getProp('objectType')) {
                case RivalIQ.enums.insightsObjectType.story:
                    title = 'Frames';
                    break;

                case RivalIQ.enums.insightsObjectType.storyDay:
                    title = 'Stories';
                    break;

                case RivalIQ.enums.insightsObjectType.post: {
                    const postWord = RivalIQ.data.channels.get(me.getProp('channel')).get('postName');
                    title = `${_.upperFirst(postWord)}s`;
                }
                    break;
                default:
                    throw new Error(`unknown insight objectType ${me.getProp('objectType')}`);
            }

            if (me.getCuration().isCustomDashboard) {
                title = [_.get(RivalIQ.enums.sourceDetails, [me.getProp('source'), 'name']), title].join(' ');
            }

            return title;
        },
        description: function () {
            const me = this;

            switch (me.getProp('objectType')) {
                case RivalIQ.enums.insightsObjectType.story:
                    return (me.getProp('storyDay') ? RivalIQ.strings.instagram_insights_stories_specific_day_table_description : RivalIQ.strings.instagram_insights_stories_table_description);

                case RivalIQ.enums.insightsObjectType.storyDay:
                    return RivalIQ.strings.instagram_insights_stories_by_day_table_description;

                case RivalIQ.enums.insightsObjectType.post: {
                    const postWord = RivalIQ.data.channels.get(me.getProp('channel')).get('postName');
                    return RivalIQ.str('insights_posts_table_description', `${postWord}s`, postWord);
                }
                default:
                    throw new Error(`unknown insight objectType ${me.getProp('objectType')}`);
            }
        },

        neverFilter: true,
        filter: function () {
            const me = this;

            const objectType = me.getProp('objectType');
            const isPostObjectType = (objectType === RivalIQ.enums.insightsObjectType.post);

            return (isPostObjectType && RivalIQ.apps.Insights.utils.isLoadingInstagramPromotedPosts(me));
        },

        tableClassName: function () {
            const me = this;

            if (!me.getProp('noClickThrough')) {
                return 'table-hover';
            }
        },

        exportFormats: RivalIQ.apps.SocialPosts.utils.gridExportFormats,

        getExportMetricDefinitions: function (format) {
            const me = this;
            return me.metricsCollection.invoke('serialize', format);
        },

        exportProps: function (props) {
            const me = this;
            return _.omit(me.mixins.Export.exportProps.call(me, props), 'postMetric');
        },

        csvExportProps: function () {
            const me = this;

            const props = me.getProps();
            const objectType = props.objectType;

            if (_.isNumber(props.orderBy)) {
                props.orderBy = me.metricsCollection.getOrderBy(props.orderBy)?.property;
            }

            let additionalMetrics;
            let typeMetrics;
            const hasPaidEngagement = (
                props.source === RivalIQ.enums.sources.INSTAGRAM_INSIGHTS &&
                objectType === RivalIQ.enums.insightsObjectType.post &&
                App.utils.hasInstagramPaidEngagement(me));

            switch (objectType) {
                case RivalIQ.enums.insightsObjectType.storyDay:
                    additionalMetrics = ['story_day_date', 'image'];
                    typeMetrics = RivalIQ.data.metrics.forSourceStoryDay(props.source);
                    break;

                case RivalIQ.enums.insightsObjectType.story:
                    additionalMetrics = [
                        'published_at',
                        'captured_at',
                        'caption',
                        'media_type',
                        'image'
                    ];
                    typeMetrics = RivalIQ.data.metrics.forSourceStory(props.source);
                    break;

                case RivalIQ.enums.insightsObjectType.post:
                    additionalMetrics = _.compact([
                        'published_at',
                        'captured_at',
                        (props.useUniversalMetricsApi ? 'company_name' : null),
                        'caption',
                        'image',
                        'postLink',
                        (hasPaidEngagement ? 'boosted_post_total_spend' : null)
                    ]);

                    if (props.source === RivalIQ.enums.sources.LINKEDIN) {
                        additionalMetrics = _.union(['linked_in_company_name'], additionalMetrics);
                    } else {
                        additionalMetrics = _.union(['presence_handle'], additionalMetrics);
                    }

                    typeMetrics = RivalIQ.data.metrics.forSourcePost(props.source, {
                        hasPaidEngagement: hasPaidEngagement
                    });
                    typeMetrics.push({
                        property: 'post_tag_ids'
                    });
                    break;
                default:
                    throw new Error(`unknown insight objectType ${me.getProp('objectType')}`);
            }

            const allProperties = additionalMetrics.concat(typeMetrics);
            const metrics = _.map(allProperties, function (metric) {
                if (_.isString(metric)) {
                    metric = {
                        property: metric
                    };
                }
                const metricAttrs = _.defaults({
                    limit: -1,
                    currency: (hasPaidEngagement ? getFacebookAdAccountCurrency(me) : undefined)
                }, metric, props);

                const metricDefinition = new RivalIQ.models.MetricDefinition(App.utils.createInsightsMetric(objectType, metricAttrs));
                let jsonMetric = metricDefinition.serialize();

                if (metricAttrs.useUniversalMetricsApi) {
                    jsonMetric = RivalIQ.universal.sanitizeMetricForXhr(jsonMetric);
                }

                return _.defaults(jsonMetric, {
                    landscapeId: RivalIQ.state.landscape.get('id')
                });
            });

            return {
                type: 'metric',
                includeDimensionColumn: false,
                includePostTagColumns: true,
                nameDimensions: me.getProp('nameDimensions'),
                metric: metrics
            };
        },

        viewMorePath: function () {
            const me = this;

            return me.getPath({
                limit: -1,
                orderBy: me.getProp('orderBy'),
                sortOrder: me.getProp('sortOrder')
            });
        },

        getSortProperties: function (objectType) {
            const me = this;
            return App.widgets.media.utils.getSortProperties(me, objectType);
        },

        processProps: function (props) {
            const me = this;

            let layout;

            // NOTE we need to only apply these defaults when outside of the
            // Custom Dashboard context (as is the case for Social Posts)
            if (isInsightsCuration(me.curation)) {
                layout = RivalIQ.apps.SocialPosts.utils.getDefaultLayoutByChannel(props.channel, me.curation.id);
            }

            if (me.isExportingPPT()) {
                props.columnLimit = MAX_PPT_EXPORT_COLUMNS;
            }

            props = _.defaults(props, {
                layout: layout,
                objectType: (props.source === RivalIQ.enums.sources.INSTAGRAM_INSIGHTS ? RivalIQ.enums.insightsObjectType.storyDay : RivalIQ.enums.insightsObjectType.post),
                omitTimeAnalysisModeDescription: true,
                showDescriptions: true,
                sortOrder: 1,
                sortable: true
            });

            props.limit = getLimit(me, props.layout, props.limit);

            const isCustomDashboard = me.getCuration().isCustomDashboard;
            const isMediaWidget = _.get(me, ['curation', 'widgetId']) === RivalIQ.enums.widgetTypes.MEDIA_WIDGET;
            if (isCustomDashboard && !props.companyId && !props.useUniversalMetricsApi && !isMediaWidget) {
                props.companyId = props.focusCompanyId;
            }

            if (props.objectType !== RivalIQ.enums.insightsObjectType.story) {
                props.mediaType = undefined;
            }

            if (!isCustomDashboard && props.source === RivalIQ.enums.sources.INSTAGRAM_INSIGHTS && props.objectType === RivalIQ.enums.insightsObjectType.post && _.isEmpty(props.post_metrics)) {
                const hasPaidEngagement = App.utils.hasInstagramPaidEngagement(me);

                if (hasPaidEngagement) {
                    props.post_metrics = [
                        'engagement_total',
                        'impressions',
                        'engagement_rate_by_impression'
                    ];
                } else {
                    props.post_metrics = [
                        'organic_engagement_total',
                        'organic_impressions',
                        'organic_engagement_rate_by_impression'
                    ];
                }
                props.orderBy = props.post_metrics[0];

                // NOTE: we need to set the configuration props because the manage columns eventing is based on these props; we'll only need to do this
                // if the user has never selected columns for this widget. Once they change the default column set, those columns will be persisted to
                // the config and we won't get into this code.
                if (_.isEmpty(me.configurationProps.post_metrics)) {
                    me.configurationProps.post_metrics = props.post_metrics;
                }
            }

            // we'll set up table metrics later when we can determine curation presence, see #27023
            if (!widgetIsCustomDashboard(me) || RivalIQ.apps.SocialPosts.utils.isGridLayoutFromProps(props)) {
                setupMetrics(me, props);
            }

            // disable table sorting while maintaining metric sorting - sortable exists on both
            if (props.sortTable !== undefined) {
                props.sortable = props.sortTable;
            }

            return props;
        },

        getPropsForSerialization: function (props) {
            const me = this;

            let propsToOmitFromSerialization = [];
            let propsToSerialize = me.propsToSerialize;
            const curation = me.getCuration();
            if (!curation.isCustomDashboard) {
                const propsToPick = _.map(RivalIQ.enums.insightsObjectType, function (objectType) {
                    return App.utils.getWidgetDefinitions(props, curation.isFocus());
                });

                return _.pick(props, propsToPick);
            } else {
                propsToSerialize = me.propsToSerialize.concat(['title']);

                if (!curation.getProp('companyId')) {
                    propsToOmitFromSerialization = ['companyId'];
                }
            }

            // NOTE we need to run our selected props through another filter so
            // that we can support an un-pinned focus company
            return _.omit(_.pick(me.getProps(), propsToSerialize), propsToOmitFromSerialization);
        },

        getValidChannels: function () {
            return RivalIQ.data.channels.withPrivatePostsData().withAccess().notHidden(true).filter(function (channel) {
                return true;
            });
        },

        getSize: function () {
            const me = this;

            const curation = me.getCuration();
            if (!me.isFocus() && curation.id === RivalIQ.enums.curation.DASHBOARDS) {
                return RivalIQ.enums.widgetSize.FULL_PAGE;
            }

            return RivalIQ.enums.widgetSize.THIRD_PAGE;
        }
    });

    MediaWidget.prepare = function (props) {

        if (!RivalIQ.enums.sourceDetails[props.source]) {
            return $.when();
        }

        const fetchProps = _.defaults({}, props, {
            companyId: props.focusCompanyId,
            channel: RivalIQ.enums.sourceDetails[props.source].channel
        });

        const fetches = [];

        let madeDataFetch = false;
        let madeInstagramFetch = false;

        const sourceDetails = RivalIQ.enums.sourceDetails[props.source];
        if (sourceDetails.privateData) {
            // NOTE when fetching private metadata we MUST be sure to pass the
            // correct channel name, as calling the underlying API on the server
            // with `cross_channel` supplied is catastrophic (see #31840)
            const privateMetadataFetchProps = _.defaults({
                channel: sourceDetails.channel
            }, fetchProps);
            fetches.push(RivalIQ.data.privateMetadata.fetch(privateMetadataFetchProps));
            madeDataFetch = true;
        }

        const isWidgetEditMode = Boolean(props.focusWidgetInstanceId);

        // If we're in widget edit mode, we need to be sure to load the Instagram Insights just in case the user changes their source to instagram.
        if (isWidgetEditMode) {
            // RivalIQ.data.privateMetadata is a cache layer, so this won't result in a duplicate fetch if we're already loading instagram.
            fetches.push(RivalIQ.data.privateMetadata.fetch(_.defaults({
                source: RivalIQ.enums.sources.INSTAGRAM_INSIGHTS,
                channel: RivalIQ.enums.channels.INSTAGRAM
            }, fetchProps)));
            madeInstagramFetch = true;
        }

        return $.when.apply($, fetches)
            .then(function () {

                let data = {};

                const metadata = (madeDataFetch ? _.head(arguments) : null);
                if (metadata) {
                    const company = _.find(metadata.companies, function (company) {
                        return (company.id === props.companyId);
                    });

                    data = {
                        metadata: metadata,
                        presence: new Backbone.Model(company)
                    };
                }

                const instagramMetadata = (madeInstagramFetch ? _.last(arguments) : null);
                if (instagramMetadata) {
                    const instagramCompany = _.find(instagramMetadata.companies, function (company) {
                        return (company.id === fetchProps.companyId);
                    });
                    data.instagramPresence = new Backbone.Model(instagramCompany);
                    data.instagramCompanies = instagramMetadata.companies;
                }

                return data;
            });
    };

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSIGHTS_MEDIA, MediaWidget);

    function getMetricsCollection(context) {
        const metrics = App.views.TableMetricChooser.prototype.getMetricsCollection.call(context);
        return metrics;
    }

    function insightsPostMediaTypeFilterItems(source) {
        RivalIQ.apps.SocialPosts.utils.getPostMediaTypeFilterItems(source);
    }

    function instagramObjectMediaTypeFilterItems(objectType) {
        let selectableItems = [
            {
                id: undefined,
                name: 'All Media'
            },
            {
                id: 'photo',
                name: 'Photo'
            },
            {
                id: 'video',
                name: 'Video'
            },
            {
                id: 'carousel',
                name: 'Carousel'
            },
            {
                id: 'reel',
                name: 'Reel'
            }
        ];

        if (objectType !== RivalIQ.enums.insightsObjectType.post) {
            selectableItems = _.reject(selectableItems, {
                id: 'carousel'
            });
        }

        return selectableItems;
    }

    App.widgets.INSIGHTS_POST_DEFAULT_METRICS = {};
    App.widgets.INSIGHTS_POST_DEFAULT_METRICS[RivalIQ.enums.sources.FACEBOOK_INSIGHTS] = ['engagement_total', 'impressions', 'engagement_rate_by_impression'];
    App.widgets.INSIGHTS_POST_DEFAULT_METRICS[RivalIQ.enums.sources.INSTAGRAM_INSIGHTS] = ['engagement_total', 'organic_reach', 'engagement_rate_by_impression'];
    App.widgets.INSIGHTS_POST_DEFAULT_METRICS[RivalIQ.enums.sources.LINKEDIN] = ['engagement_total', 'impressions', 'engagement_rate_by_presence_reach'];
    App.widgets.INSIGHTS_POST_DEFAULT_METRICS[RivalIQ.enums.sources.TWITTER_INSIGHTS] = ['engagement_total', 'impressions', 'engagement_rate_by_impression'];
    App.widgets.INSIGHTS_POST_DEFAULT_METRICS[RivalIQ.enums.sources.TIKTOK_INSIGHTS] = ['engagement_total', 'impressions', 'engagement_rate_by_impression'];

    const PostsTable = MediaWidget.extend({
        processProps: function (props) {
            const me = this;

            _.extend(props, {
                objectType: RivalIQ.enums.insightsObjectType.post
            });

            if (!props.source) {
                switch (props.channel) {
                    case RivalIQ.enums.channels.INSTAGRAM:
                        props.source = RivalIQ.enums.sources.INSTAGRAM_INSIGHTS;
                        break;

                    case RivalIQ.enums.channels.LINKEDIN:
                        props.source = RivalIQ.enums.sources.LINKEDIN;
                        break;

                    case RivalIQ.enums.channels.TWITTER:
                        props.source = RivalIQ.enums.sources.TWITTER_INSIGHTS;
                        break;
                    default:
                        throw new Error(`Posts Table - unknown source ${props.source}`);
                }

                if (!props.source) {
                    props.source = RivalIQ.enums.sources.INSTAGRAM_INSIGHTS;
                }
            }
            props.channel = RivalIQ.enums.sourceDetails[props.source].channel;

            return MediaWidget.prototype.processProps.call(me, props);
        },

        // The rest of the methods in this file are deprecated, remove them after migrating all the existing insta posts tables
        // to the focus company table widget.
        getBuilderFields: function () {
            const me = this;

            const fields = [
                {
                    type: 'source_only'
                },
                {
                    type: 'postFilter'
                },
                {
                    type: 'postType',
                    prop: 'mediaTypeFilter',
                    items: function () {
                        return insightsPostMediaTypeFilterItems(me.getProp('source'));
                    },
                    onBaseMetricChanged: function (view) {
                        view.reload();
                    }
                },
                {
                    type: 'layout'
                },
                {
                    type: 'metrics',
                    size: 12,
                    title: 'Metrics',
                    loadData: function () {
                        const field = this;

                        const props = me.getProps();

                        if (props.source === RivalIQ.enums.sources.INSTAGRAM_INSIGHTS) {
                            // need to load metadata to see if this account has access to paid metrics.
                            return RivalIQ.data.privateMetadata.fetch(props)
                                .then(function (metadata) {
                                    const company = _.find(metadata.companies, function (company) {
                                        return (company.id === props.companyId);
                                    });
                                    me.curation.presence = new Backbone.Model(company);
                                    me._updateValidMetricsForBuilder(field);
                                });
                        } else {
                            // for the other channels, we don't need any other info to decide what the metrics should be.
                            me._updateValidMetricsForBuilder(field);
                            return $.when();
                        }
                    },
                    onBaseMetricChanged: function (view) {
                        view.reload();
                    }
                }
            ];

            return fields;
        },

        _updateValidMetricsForBuilder: function (field) {
            const me = this;

            // We need to get the metrics via table metric chooser (instead of just looking at props) because we do some metric remapping there
            // if the user has access to ads and this is instagram insights.
            let metrics = App.views.TableMetricChooser.prototype.getMetricsCollection.call(me);
            const allMetricsValid = me.areAllMetricsValid(metrics);

            if (!allMetricsValid) {
                // if any of the metrics aren't valid, then reset all the metrics. This happens when you change source to a source that doesn't
                // have one of the metrics (e.g., if you had 30-second views in fb then changed to another channel, no other channel has that).
                const newMetrics = _.map(App.widgets.INSIGHTS_POST_DEFAULT_METRICS[me.getProp('source')], function (property) {
                    return {
                        property: property
                    };
                });
                me.curation.setProps({
                    metrics: newMetrics
                }, {
                    navigate: false
                });
                metrics = App.views.TableMetricChooser.prototype.getMetricsCollection.call(me);
            }
            field.set({
                metrics: metrics
            });
        },

        areAllMetricsValid: function (metrics) {
            const me = this;

            const props = me.getProps();
            const allMetrics = RivalIQ.apps.Favorites.data.metrics.getMetricSelectOptions(me, props.source, props, me.baseMetric);

            return metrics.all(function (metric) {
                return Boolean(_.find(allMetrics, function (knownMetric) {
                    return knownMetric.metric.property === metric.get('metric').property;
                }));
            });
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_POSTS, PostsTable);

    const StoryDaysTable = MediaWidget.extend({
        processProps: function (props) {
            const me = this;
            _.extend(props, {
                objectType: RivalIQ.enums.insightsObjectType.storyDay
            });
            props.source = RivalIQ.enums.sources.INSTAGRAM_INSIGHTS;
            props.channel = RivalIQ.enums.sourceDetails[props.source].channel;
            return MediaWidget.prototype.processProps.call(me, props);
        },
        getBuilderFields: function () {
            const me = this;
            return [
                {
                    type: 'postFilter',
                    title: 'Search Story Content'
                },
                {
                    type: 'layout'
                },
                {
                    type: 'metrics',
                    size: 12,
                    title: 'Metrics',
                    metrics: getMetricsCollection(me)
                }
            ];
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_STORYDAYS, StoryDaysTable);

    const StoriesTable = MediaWidget.extend({
        processProps: function (props) {
            const me = this;
            _.extend(props, {
                objectType: RivalIQ.enums.insightsObjectType.story
            });
            props.source = RivalIQ.enums.sources.INSTAGRAM_INSIGHTS;
            props.channel = RivalIQ.enums.sourceDetails[props.source].channel;
            return MediaWidget.prototype.processProps.call(me, props);
        },
        getBuilderFields: function () {
            const me = this;
            const items = _.partial(instagramObjectMediaTypeFilterItems, me.getProp('objectType'));
            return [
                {
                    type: 'postFilter',
                    title: 'Search Story Content'
                },
                {
                    type: 'postType',
                    title: 'Frame Type',
                    prop: 'mediaTypeFilter',
                    items: items
                },
                {
                    type: 'layout'
                },
                {
                    type: 'metrics',
                    size: 12,
                    title: 'Metrics',
                    metrics: getMetricsCollection(me)
                }
            ];
        }
    });

    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.INSTAGRAM_INSIGHTS_STORIES, StoriesTable);

}, window.s, window.moment);
